<script setup>
  import {computed} from "vue";

  import ComponentTableRowCheckbox from "@components/Table/TableRowCheckbox.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import PatientsPatientInformation from "@pages/Patients/Components/PatientInformation.vue";
  import {MedBadge, MedButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientRow",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    selectedRows: {
      type: Array,
      required: true,
    },
  });

  const emit = defineEmits(["open:patientNote", "open:patientTodos", "confirm:delete", "update:selectedRows"]);

  const page = usePage();

  function openPatientNote() {
    emit("open:patientNote");
  }

  function openPatientTodos() {
    emit("open:patientTodos");
  }

  function confirmDelete() {
    emit("confirm:delete");
  }

  function clickRow(event, href) {
    let node = event.target ?? event;

    if (!node.nodeName) {
      return;
    }

    if (node.nodeName.toLowerCase() === "td") {
      // click on <td> => open link
      router.visit(href);
      return;
    }

    if (
      node.nodeName.toLowerCase() === "a" ||
      node.nodeName.toLowerCase() === "button" ||
      node.nodeName.toLowerCase() === "select" ||
      node.nodeName.toLowerCase() === "input" ||
      node.nodeName.toLowerCase() === "textarea" ||
      node.nodeName.toLowerCase() === "dialog"
    ) {
      // click on a clickable element (a or button)
      return;
    }

    if (node.parentNode) {
      // click on any other element: check if parent is <td>
      return clickRow(node.parentNode, href);
    }
  }

  const isDeletable = computed(() => {
    if (props.patient.from_wawi) {
      return page.props.can.delete_wawi_patients && props.patient.wawi_records_count === 0;
    }

    return page.props.can.delete_patients;
  });

  const deleteTooltipText = computed(() => {
    if (isDeletable.value) {
      return "Löschen";
    }

    if (props.patient.from_wawi) {
      if (page.props.can.delete_wawi_patients) {
        return "Sie können den Patienten nicht löschen, da dieser noch Medikationsanalysen aus der Warenwirtschaft hat.";
      }

      return "Sie können den Patienten nicht löschen, da dieser aus der Warenwirtschaft übertragen wurde";
    }

    return "Sie können den Patienten nicht löschen, da Berechtigungen fehlen.";
  });
</script>

<template>
  <tr
    class="cursor-pointer border-b-2 border-gray-200 leading-tight hover:bg-gray-100"
    :class="{'bg-gray-200': selectedRows.includes(patient.id)}"
    :data-test="getTestId('patient-row')"
    @click="(event) => clickRow(event, $route('patients.show', {patient: patient.id}))"
  >
    <!-- ☑️ multi-select & patient -->
    <td class="px-0.5 py-2">
      <MedTooltip v-if="patient.suffix || patient.prefix" content="Nameszusatz">
        <span class="text-sm" :class="{'ml-8': $page.props?.can?.allow_multi_select}">
          {{ patient.suffix }} {{ patient.prefix }}
        </span>
      </MedTooltip>

      <div class="flex items-center space-x-2">
        <ComponentTableRowCheckbox
          v-if="$page.props?.can?.allow_multi_select"
          :row-key="patient.id"
          :selected-rows="selectedRows"
          :data-test="getTestId('patient-row')"
          @update:selected-rows="(newSelectedRows) => $emit('update:selectedRows', newSelectedRows)"
        />

        <MedLink
          as="inertia"
          class="text-lg font-semibold"
          :href="$route('patients.show', {patient: patient.id})"
          :data-test="getTestId('patient-row-show-patient-inertia-link')"
        >
          {{ patient.lastname }}, {{ patient.firstname }}
        </MedLink>

        <MedTooltip :content="patient.note === null ? 'Patientennotiz anlegen' : 'Patientennotiz öffnen'">
          <div class="relative">
            <MedBadge variant="secondary" :dot="patient.note !== null" :data-test="getTestId('patient-row-note-badge')">
              <MedButton
                :aria-label="patient.lastname ? `Notiz für ${patient.lastname} öffnen` : 'Patientennotiz öffnen'"
                flat
                icon="assignment"
                size="xs"
                rounded="full"
                :data-test="getTestId('patient-row-open-note-button')"
                @click="openPatientNote()"
              />
            </MedBadge>
          </div>
        </MedTooltip>
      </div>

      <div class="flex items-center space-x-2" :class="{'ml-8': $page.props?.can?.allow_multi_select}">
        <MedTooltip content="Geburtsdatum">
          <span class="text-xs">
            {{ patient.birthdate_formatted }}
          </span>
        </MedTooltip>

        <MedTooltip content="Versichertennummer">
          <span class="text-xs">
            {{ patient.insurancenumber }}
          </span>
        </MedTooltip>
      </div>
    </td>

    <!-- status -->
    <td class="px-0.5 py-2">
      <PatientsPatientInformation :patient="patient" />
    </td>

    <!-- to dos -->
    <td v-if="$page.props.can.view_todos" class="px-0.5 py-2 text-sm">
      <MedTooltip v-if="patient?.todo_count === 0" content="Neue Aufgabe anlegen">
        <div class="inline-block">
          <MedButton
            :aria-label="patient.lastname ? `Neue Aufgabe für ${patient.lastname} anlegen` : 'Neue Aufgabe anlegen'"
            flat
            icon="add"
            size="xs"
            rounded="full"
            :data-test="getTestId('patient-row-open-todo-button')"
            @click="openPatientTodos"
          />
        </div>
      </MedTooltip>

      <template v-if="patient?.todo_count > 0">
        <MedLink as="button" :data-test="getTestId('patient-row-open-todo-button')" @click.prevent="openPatientTodos()">
          {{ patient?.todo_last?.label }}
        </MedLink>
      </template>

      <div v-if="patient?.todo_count > 1" class="text-xs">
        und
        <MedLink
          as="button"
          variant="primary"
          :data-test="getTestId('patient-row-open-todo-button')"
          @click.prevent="openPatientTodos()"
        >
          {{ patient?.todo_count - 1 }} weitere
        </MedLink>
      </div>
    </td>

    <!-- last updates -->
    <td class="px-0.5 py-2">
      {{ patient.updated_at }}

      <div v-if="patient.username" class="text-xs">
        {{ patient.username }}
      </div>
    </td>

    <!-- actions -->
    <td class="px-0.5 py-2">
      <div class="flex justify-center">
        <MedTooltip :content="deleteTooltipText">
          <MedButton
            :aria-label="`Patient ${patient.lastname} löschen`"
            flat
            icon="delete"
            rounded="full"
            :disabled="!isDeletable"
            :data-test="getTestId('patient-row-delete-button')"
            @click="confirmDelete()"
          />
        </MedTooltip>
      </div>
    </td>
  </tr>
</template>
